import { parseUiData } from "@/flow/utils/uiDataUtils";

/**
 * Find a router step by one of its route IDs
 */
export function findRouterByRouteId(steps: any[], routeId: string): any | null {
  if (!steps || !routeId) return null;

  for (let i = 0; i < steps.length; i += 1) {
    const step = steps[i];
    const routes = step?.routes;

    if (Array.isArray(routes)) {
      if (step?.type === "ROUTER" && routes.some((r) => r?.id === routeId)) {
        // Hydrate loop context from uiData (it may have changed after edits).
        populateRouterLoopContext(step);
        return step;
      }

      for (let j = 0; j < routes.length; j += 1) {
        const nestedSteps = routes[j]?.steps;
        if (Array.isArray(nestedSteps)) {
          const nested = findRouterByRouteId(nestedSteps, routeId);
          if (nested) return nested;
        }
      }
    }
  }

  return null;
}

/**
 * Check if a router node is inside a loop
 */
export function isRouterInsideLoop(routerNode: any): boolean {
  if (!routerNode?.data) return false;

  if (routerNode.data.insideLoop) return true;
  if (routerNode.data.loopEndOrigId) return true;

  if (routerNode.data.uiData) {
    try {
      const uiData = JSON.parse(routerNode.data.uiData);
      return uiData.insideLoop === true;
    } catch (e) {
      return false;
    }
  }

  return false;
}

/**
 * Populate router's loop context from uiData onto the step object
 */
export function populateRouterLoopContext(router: any): void {
  if (!router || router.type !== "ROUTER") return;

  // Routers can be moved in/out of loops. In some edit flows (attach/detach),
  // uiData can temporarily be null while loop context is kept on the step
  // object (insideLoop/loopEndOrigId/parentLoopOrigId). In that case, do not
  // wipe those fields here; downstream sync logic will validate connectivity
  // via edges to avoid stale hidden edges.
  if (!router.uiData) return;

  // Routers can be moved in/out of loops; always rehydrate loop fields from uiData.
  try {
    const uiData = parseUiData(router.uiData);
    const insideLoop = uiData?.insideLoop === true;

    if (!insideLoop) {
      delete router.insideLoop;
      delete router.parentLoopOrigId;
      delete router.loopEndOrigId;
      return;
    }

    router.insideLoop = true;

    if (uiData?.parentLoopOrigId) {
      router.parentLoopOrigId = uiData.parentLoopOrigId;
    } else {
      delete router.parentLoopOrigId;
    }
    if (uiData?.loopEndOrigId) {
      router.loopEndOrigId = uiData.loopEndOrigId;
    } else {
      delete router.loopEndOrigId;
    }
  } catch (e) {
    // ignore parse error
  }
}

/**
 * Get router's loop context (loopEndOrigId, parentLoopOrigId)
 */
export function getRouterLoopContext(router: any): {
  insideLoop: boolean;
  loopEndOrigId: string | null;
  parentLoopOrigId: string | null;
} {
  const noContext = {
    insideLoop: false,
    loopEndOrigId: null,
    parentLoopOrigId: null,
  };

  if (!router || router.type !== "ROUTER") return noContext;

  // Keep loop context in sync with uiData when present (but don't mutate when uiData is missing)
  if (router.uiData) {
    populateRouterLoopContext(router);
  }

  // First check direct properties
  if (router.insideLoop && router.loopEndOrigId) {
    return {
      insideLoop: true,
      loopEndOrigId: router.loopEndOrigId,
      parentLoopOrigId: router.parentLoopOrigId || null,
    };
  }

  // Then check uiData
  if (router.uiData) {
    try {
      const uiData = JSON.parse(router.uiData);
      if (uiData.insideLoop && uiData.loopEndOrigId) {
        return {
          insideLoop: true,
          loopEndOrigId: uiData.loopEndOrigId,
          parentLoopOrigId: uiData.parentLoopOrigId || null,
        };
      }
    } catch (e) {
      // ignore
    }
  }

  return noContext;
}

/**
 * Get all step origIds within a router (including nested routers' steps)
 */
export function collectAllRouterStepOrigIds(router: any): Set<string> {
  const ids = new Set<string>();

  if (!router?.routes) return ids;

  const collectFromSteps = (steps: any[]) => {
    if (!Array.isArray(steps)) return;

    steps.forEach((step) => {
      if (step.origId) ids.add(step.origId);

      // If step is a nested router, collect its steps too
      if (step.type === "ROUTER" && step.routes) {
        step.routes.forEach((route: any) => {
          collectFromSteps(route.steps || []);
        });
      }
    });
  };

  router.routes.forEach((route: any) => {
    collectFromSteps(route.steps || []);
  });

  return ids;
}

/**
 * Get exit step origId for each route in a router
 * Exit step = last step in each route
 */
export function getRouterExitSteps(router: any): string[] {
  const exitSteps: string[] = [];

  if (!router?.routes) return exitSteps;

  router.routes.forEach((route: any) => {
    if (route.steps && route.steps.length > 0) {
      const lastStep = route.steps[route.steps.length - 1];
      if (lastStep?.origId) {
        exitSteps.push(lastStep.origId);
      }
    }
  });

  return exitSteps;
}

function isVisibleRightEdge(edge: any): boolean {
  return Boolean(
    edge &&
      !edge.hidden &&
      typeof edge.sourceHandle === "string" &&
      edge.sourceHandle.includes("__handle-right")
  );
}

function getConnectedRouteExitStep(
  router: any,
  route: any,
  edges: any[],
  routerStepIds: Set<string>
): string | null {
  const routeSteps = Array.isArray(route?.steps)
    ? route.steps
        .map((step: any) => normalizeEdgeValue(step?.origId))
        .filter(Boolean)
    : [];

  if (routeSteps.length === 0) return null;

  const routeEntryTargets = new Set<string>(
    edges
      .filter((edge) => {
        if (!edge || edge.hidden) return false;
        if (edge.source !== router?.origId) return false;
        if (typeof edge.sourceHandle !== "string") return false;
        return edge.sourceHandle.includes("__handle-bottom");
      })
      .map((edge) => normalizeEdgeValue(edge?.target))
      .filter(Boolean)
  );

  const routeStartOrigId =
    routeSteps.find((stepOrigId) => routeEntryTargets.has(stepOrigId)) ??
    routeSteps[0];

  if (!routeEntryTargets.has(routeStartOrigId)) {
    return null;
  }

  let currentOrigId = routeStartOrigId;
  const visited = new Set<string>();

  while (currentOrigId && !visited.has(currentOrigId)) {
    visited.add(currentOrigId);
    const currentRouteStepOrigId = currentOrigId;

    const nextRightEdge = edges.find((edge) => {
      if (!isVisibleRightEdge(edge)) return false;
      if (normalizeEdgeValue(edge?.source) !== currentRouteStepOrigId) {
        return false;
      }

      const targetOrigId = normalizeEdgeValue(edge?.target);
      return Boolean(targetOrigId && routerStepIds.has(targetOrigId));
    });

    if (!nextRightEdge) {
      return currentOrigId;
    }

    const nextOrigId = normalizeEdgeValue(nextRightEdge?.target);
    if (!nextOrigId || visited.has(nextOrigId)) {
      return currentOrigId;
    }

    currentOrigId = nextOrigId;
  }

  return currentOrigId || null;
}

function getConnectedRouteExitSteps(router: any, edges: any[]): string[] {
  const routerStepIds = collectAllRouterStepOrigIds(router);
  const exitSteps = new Set<string>();

  if (!Array.isArray(router?.routes) || !Array.isArray(edges)) {
    return [];
  }

  router.routes.forEach((route: any) => {
    const exitStepOrigId = getConnectedRouteExitStep(
      router,
      route,
      edges,
      routerStepIds
    );

    if (exitStepOrigId && exitStepOrigId !== router.origId) {
      exitSteps.add(exitStepOrigId);
    }
  });

  return Array.from(exitSteps);
}

/**
 * Find all routers in the step list (including nested ones)
 */
export function findAllRouters(steps: any[]): any[] {
  const routers: any[] = [];

  if (!Array.isArray(steps)) return routers;

  steps.forEach((step) => {
    if (step.type === "ROUTER") {
      populateRouterLoopContext(step);
      routers.push(step);

      // Check nested routers inside this router's routes
      if (step.routes) {
        step.routes.forEach((route: any) => {
          const nestedRouters = findAllRouters(route.steps || []);
          routers.push(...nestedRouters);
        });
      }
    }
  });

  return routers;
}

/**
 * Find all routers that are inside loops
 */
export function findAllRoutersInsideLoops(steps: any[]): any[] {
  const allRouters = findAllRouters(steps);
  return allRouters.filter((router) => {
    const context = getRouterLoopContext(router);
    return context.insideLoop && context.loopEndOrigId;
  });
}

function normalizeEdgeValue(value: unknown): string {
  return String(value ?? "");
}

function isHiddenExitConnectionEdge(edge: any): boolean {
  return Boolean(
    edge?.hidden &&
      (edge?.data?.exitConnection ||
        (typeof edge?.id === "string" && edge.id.startsWith("hidden-")))
  );
}

function getVisibleEdgeSignature(edges: any[]): string {
  if (!Array.isArray(edges)) return "";

  return edges
    .filter((edge) => edge && !edge.hidden)
    .map((edge) =>
      [
        normalizeEdgeValue(edge?.source),
        normalizeEdgeValue(edge?.target),
        normalizeEdgeValue(edge?.sourceHandle),
        normalizeEdgeValue(edge?.targetHandle),
      ].join("::")
    )
    .sort()
    .join("||");
}

export function getHiddenExitEdgeSignature(edges: any[]): string {
  if (!Array.isArray(edges)) return "";

  return edges
    .filter((edge) => isHiddenExitConnectionEdge(edge))
    .map((edge) =>
      [
        normalizeEdgeValue(edge?.source),
        normalizeEdgeValue(edge?.target),
        normalizeEdgeValue(edge?.sourceHandle),
        normalizeEdgeValue(edge?.targetHandle),
      ].join("::")
    )
    .sort()
    .join("||");
}

export function getRouterExitSyncSignature(
  stepList: any[],
  edges: any[]
): string {
  const routersInLoops = findAllRoutersInsideLoops(stepList);

  return routersInLoops
    .map((router) => {
      const context = getRouterLoopContext(router);
      const relevantIds = new Set<string>([
        normalizeEdgeValue(router?.origId),
        normalizeEdgeValue(context.parentLoopOrigId),
        normalizeEdgeValue(context.loopEndOrigId),
      ]);

      const routeSignature = Array.isArray(router?.routes)
        ? router.routes
            .map((route: any) => {
              const routeStepIds = Array.isArray(route?.steps)
                ? route.steps
                    .map((step: any) => normalizeEdgeValue(step?.origId))
                    .filter(Boolean)
                : [];

              routeStepIds.forEach((stepId: string) => relevantIds.add(stepId));

              return [normalizeEdgeValue(route?.id), ...routeStepIds].join(">");
            })
            .sort()
            .join("|")
        : "";

      const relevantVisibleEdges = Array.isArray(edges)
        ? edges
            .filter((edge) => edge && !edge.hidden)
            .filter((edge) => {
              const source = normalizeEdgeValue(edge?.source);
              const target = normalizeEdgeValue(edge?.target);
              return relevantIds.has(source) || relevantIds.has(target);
            })
            .map((edge) =>
              [
                normalizeEdgeValue(edge?.source),
                normalizeEdgeValue(edge?.target),
                normalizeEdgeValue(edge?.sourceHandle),
                normalizeEdgeValue(edge?.targetHandle),
              ].join("::")
            )
            .sort()
            .join("|")
        : "";

      return [
        normalizeEdgeValue(router?.origId),
        normalizeEdgeValue(context.parentLoopOrigId),
        normalizeEdgeValue(context.loopEndOrigId),
        routeSignature,
        relevantVisibleEdges,
        getVisibleEdgeSignature(edges),
      ].join("::");
    })
    .sort()
    .join("||");
}

/**
 * Synchronize all hidden exit edges for all routers inside loops.
 * Manage hidden exit edges.
 *
 * @param stepList - The current step list
 * @param edges - The current edges array (will be mutated)
 * @returns The updated edges array
 */
export function syncAllRouterExitConnections(
  stepList: any[],
  edges: any[]
): any[] {
  // Find all routers that are inside loops
  const routersInLoops = findAllRoutersInsideLoops(stepList);

  // Only sync routers that are currently connected to their LOOP_END to avoid stale hidden edges.
  const connectedRouters = routersInLoops.filter((router) => {
    const context = getRouterLoopContext(router);
    if (!context.insideLoop || !context.loopEndOrigId) return false;
    return edges.some((e) => {
      if (e?.source !== router.origId) return false;
      if (e?.target !== context.loopEndOrigId) return false;

      // Router can be connected to LOOP_END via a visible edge (preferred) or
      // via a hidden exitConnection edge when it's nested as another router's
      // route-exit step. In both cases we still need to sync exit-step hidden edges.
      if (e?.hidden !== true) return true;

      return Boolean(
        e?.data?.exitConnection ||
          (typeof e?.id === "string" && e.id.startsWith("hidden-"))
      );
    });
  });

  const activeRouterStepIds = new Set<string>();
  connectedRouters.forEach((router) => {
    collectAllRouterStepOrigIds(router).forEach((id) => {
      activeRouterStepIds.add(id);
    });
  });

  // Remove hidden exit edges whose sources are no longer part of active router routes
  for (let i = edges.length - 1; i >= 0; i--) {
    const edge = edges[i];
    if (isHiddenExitConnectionEdge(edge)) {
      if (
        typeof edge?.source !== "string" ||
        !activeRouterStepIds.has(edge.source)
      ) {
        edges.splice(i, 1);
      }
    }
  }

  connectedRouters.forEach((router) => {
    const context = getRouterLoopContext(router);
    if (!context.insideLoop || !context.loopEndOrigId) return;

    const { loopEndOrigId } = context;
    const allStepIds = collectAllRouterStepOrigIds(router);

    // STEP 1: Remove ALL existing hidden exit edges from this router's steps
    for (let i = edges.length - 1; i >= 0; i--) {
      const edge = edges[i];
      if (
        isHiddenExitConnectionEdge(edge) &&
        typeof edge?.source === "string" &&
        allStepIds.has(edge.source)
      ) {
        edges.splice(i, 1);
      }
    }

    // STEP 2: Add hidden edge from each route's exit step to loopEndOrigId
    const exitSteps = getConnectedRouteExitSteps(router, edges).filter(
      (exitStepOrigId) => {
        if (!exitStepOrigId || exitStepOrigId === router.origId) return false;

        // If the exit step already has a visible outgoing connection from the right handle,
        // it must not also have a hidden exit connection.
        const hasVisibleRightOutgoing = edges.some((e) => {
          if (!e || e.hidden) return false;
          if (e.source !== exitStepOrigId) return false;
          return (
            typeof e.sourceHandle === "string" &&
            e.sourceHandle.includes("__handle-right")
          );
        });
        if (hasVisibleRightOutgoing) return false;

        const route = router.routes?.find(
          (r: any) =>
            Array.isArray(r?.steps) &&
            r.steps.some((s: any) => s?.origId === exitStepOrigId)
        );

        if (!route || !Array.isArray(route.steps)) return false;

        const routeStepIds = new Set<string>();
        route.steps.forEach((s: any) => {
          if (s?.origId) routeStepIds.add(s.origId);
        });

        // Only keep exit steps that are still connected to this route (router or route step incoming)
        return edges.some(
          (e) =>
            e.target === exitStepOrigId &&
            !e.hidden &&
            (e.source === router.origId || routeStepIds.has(e.source))
        );
      }
    );

    exitSteps.forEach((exitStepOrigId) => {
      // Check if hidden edge already exists (shouldn't after step 1, but safety check)
      const exists = edges.some(
        (e) =>
          e.source === exitStepOrigId && e.target === loopEndOrigId && e.hidden
      );

      if (!exists) {
        edges.push(createHiddenEdge(exitStepOrigId, loopEndOrigId));
      }
    });
  });

  return edges;
}

/**
 * Create a hidden edge object
 */
export function createHiddenEdge(
  source: string,
  target: string,
  sourceHandle = "right",
  targetHandle = "left"
): any {
  return {
    id: `hidden-${source}-${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: `${source}__handle-${sourceHandle}`,
    targetHandle: `${target}__handle-${targetHandle}`,
    animated: false,
    hidden: true,
    data: { hidden: true, exitConnection: true },
    markerEnd: {
      type: "arrowclosed",
      width: 0,
      color: "transparent",
    },
  };
}

/**
 * Validate and fix hidden edges when loading from backend
 * For future refs
 */
export function validateAndFixHiddenEdges(
  stepList: any[],
  edges: any[]
): any[] {
  // Simply run the sync function - it will remove invalid and add missing hidden edges
  return syncAllRouterExitConnections(stepList, edges);
}

/**
 * Checks if a router/step is nested inside another router within the SAME loop.
 * Used to determine showing the right handle and line from ROUTER to LOOP_END.
 */
export function isNestedRouterInSameLoop(
  data: any,
  stepList: any[],
  parentLoopOrigId?: string
): boolean {
  if (!data?.parentId) return false;

  // Get the loop context
  let loopOrigId = parentLoopOrigId;
  if (!loopOrigId) {
    const uiData = parseUiData(data.uiData);
    loopOrigId = uiData?.parentLoopOrigId;
  }

  if (!loopOrigId) return false;

  // Find the parent router
  const parentRouter = findRouterByRouteId(stepList, data.parentId);
  if (!parentRouter) return false;

  // Check if parent router is inside the same loop
  const parentUiData = parseUiData(parentRouter.uiData);
  if (!parentUiData?.insideLoop) return false;

  return parentUiData.parentLoopOrigId === loopOrigId;
}

/**
 * Checks if a step/router is nested inside a router route AND that owning router
 * is inside the exact same loop context (parentLoopOrigId + loopEndOrigId).
 *
 * This is stricter than isNestedRouterInSameLoop and works even when routers
 * store loop context on direct fields (insideLoop/parentLoopOrigId/loopEndOrigId)
 * while uiData may be missing.
 */
export function isNestedInsideRouterInSameLoop(
  data: any,
  stepList: any[],
  loopOrigId: string | null | undefined,
  loopEndOrigId: string | null | undefined
): boolean {
  if (!data?.parentId) return false;
  if (!loopOrigId || !loopEndOrigId) return false;

  const owningRouter = findRouterByRouteId(stepList, data.parentId);
  if (!owningRouter) return false;

  const routerContext = getRouterLoopContext(owningRouter);
  return (
    routerContext.insideLoop === true &&
    routerContext.parentLoopOrigId === loopOrigId &&
    routerContext.loopEndOrigId === loopEndOrigId
  );
}
